import { DateTime } from "luxon"
import { Form, Formik } from "formik"
import { makeStyles } from "tss-react/mui"
import { DatePicker } from "@mui/x-date-pickers"
import { DialogContent, Grid } from "@mui/material"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { CustomJob, CustomReportsType, RunFormValues } from "../customReportsTypes"
import { AccountingCustomReportsQuery, runCustomJob } from "../customReportsApi"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { MerchantSettingsCustomJobQuery } from "../../../../admin/settings/customJobs/customJobApi"
import { useDateTime } from "../../../../../localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "1rem 8rem",
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        justifyContent: "space-between",
    },
    button: {
        margin: "0 auto",
        padding: "0.5rem 3.75rem",
    },
    subTitleColor: {
        color: theme.palette.text.primary,
    },
}))
type StandardRunCustomJobDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    item: CustomJob
}>

export function StandardRunCustomJobDialog({ onClose, open, item }: StandardRunCustomJobDialogProps) {
    const { classes } = useStyles()
    const isShowDateFields = item.type === CustomReportsType.ACCOUNTING || item.type === CustomReportsType.REPORT

    const { mutate: callRunCustomJob } = useApiMutation(runCustomJob, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            onClose()
            await context.queryClient.invalidateQueries({
                queryKey: [
                    AccountingCustomReportsQuery.CUSTOM_REPORTS_LIST,
                    MerchantSettingsCustomJobQuery.CUSTOM_JOBS_LIST,
                ],
            })
        },
    })

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="merchant-accounting-custom-reports-run-modal-form-title">
                    <>Download report</>
                </LocalizedStrict>
            }
            subtitle={
                <>
                    <LocalizedStrict id="merchant-accounting-custom-reports-run-modal-form-subtitle-name">
                        <>Name</>
                    </LocalizedStrict>
                    <span className={classes.subTitleColor}> {item.name}</span>
                </>
            }
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            scroll="body"
        >
            {isShowDateFields ? (
                <RunFormContent
                    handleSubmit={(values: RunFormValues) => {
                        callRunCustomJob({
                            jobId: item.id,
                            startDate: values.startDate.toISODate(),
                            endDate: values.endDate.toISODate(),
                        })
                    }}
                />
            ) : (
                <DialogContent className={classes.dialogContent}>
                    <LocalizedStrict id="merchant-accounting-custom-reports-run-form-save-button">
                        <StandardButton
                            fullWidth={false}
                            onClick={() =>
                                callRunCustomJob({
                                    jobId: item.id,
                                })
                            }
                            updateDefaultClasses={classes.button}
                        >
                            Download
                        </StandardButton>
                    </LocalizedStrict>
                </DialogContent>
            )}
        </StandardDialog>
    )
}

function RunFormContent({ handleSubmit }: { handleSubmit: (values: RunFormValues) => void }) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()

    const initialValues = {
        startDate: today,
        endDate: today,
    }

    return (
        <Formik<RunFormValues>
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnChange={false}
        >
            {({ values, setFieldValue }) => (
                <Form>
                    <DialogContent className={classes.dialogContent}>
                        <Grid container gap="1rem" justifyContent="center" flexWrap="nowrap">
                            <LocalizedStrict
                                id="merchant-accounting-custom-reports-run-modal-form-startDate-date-picker"
                                attrs={{ label: true }}
                            >
                                <DatePicker
                                    value={values.startDate}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setFieldValue("startDate", newValue)
                                        }
                                    }}
                                    label="Start date"
                                    maxDate={values.endDate}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="merchant-accounting-custom-reports-run-modal-form-endDate-date-picker"
                                attrs={{ label: true }}
                            >
                                <DatePicker
                                    value={values.endDate}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setFieldValue("endDate", newValue)
                                        }
                                    }}
                                    label="End date"
                                    minDate={values.startDate}
                                />
                            </LocalizedStrict>
                        </Grid>
                        <LocalizedStrict id="merchant-accounting-custom-reports-run-form-save-button">
                            <StandardButton fullWidth={false} type="submit" updateDefaultClasses={classes.button}>
                                Download
                            </StandardButton>
                        </LocalizedStrict>
                    </DialogContent>
                </Form>
            )}
        </Formik>
    )
}
